// Cel die telling + recente entries toont uit een Firestore-collectie. Altijd zichtbaar.
// Geanonimiseerd: alleen timestamps, count, en categorisch Type-veld.
//
// AI-CONTEXT: zie docs/patterns.md#retention-derivation

import { useMemo } from 'react';
import { useFirestoreNoCompagnyWithStatus } from '../../firebase/useFirestore';
import { daysSince, daysColorClass, formatDayAge } from '../../utils/activityAge';
import { Spinner } from './RetentionScoreCells.jsx';

const DATE_FORMATTER = new Intl.DateTimeFormat('nl-NL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
});

const formatDate = (ts) => {
    if (!ts || typeof ts.toDate !== 'function') return '—';
    return DATE_FORMATTER.format(ts.toDate());
};

/**
 * @param {object} props
 * @param {string} props.label - Kop van de kaart
 * @param {string} props.collection - Collectie die de kaart zelf leest (genegeerd met `source`)
 * @param {string} [props.compagnyId] - Project waarvan de entries getoond worden
 * @param {string} [props.noun] - Enkelvoud voor de telling
 * @param {string} [props.nounPlural] - Meervoud voor de telling
 * @param {boolean} [props.loading] - De pagina laadt het project nog
 * @param {number} [props.topN] - Hoeveel recente entries er onder elkaar staan
 * @param {(doc: object) => boolean} [props.filter] - Welke documenten meetellen
 * @param {{docs: Array<object>|'', error: {code?: string}|null, loading: boolean}} [props.source] - Door de pagina aangeleverde gegevens (bijv. via een Cloud Function); dan leest de kaart zelf niets
 */
const LastEntriesCard = ({ label, collection, compagnyId, noun = 'entry', nounPlural, loading, topN = 5, filter, source }) => {
    // Wacht met fetchen tot er een echte ID is — voorkomt verspilde reads.
    // '' is het signaal voor "nog geen waarde"; null is een geldige zoekwaarde
    // en wordt door de engine wél uitgevoerd.
    // Levert de pagina zelf de gegevens aan (`source`), dan leest de kaart
    // niets: een null-collectie slaat de listener over.
    const own = useFirestoreNoCompagnyWithStatus(
        source ? null : collection,
        'CompagnyID',
        compagnyId || ''
    );
    const { docs, error, loading: queryLoading } = source || own;

    const filtered = useMemo(() => {
        if (!Array.isArray(docs)) return null;
        return filter ? docs.filter(filter) : docs;
    }, [docs, filter]);

    const sorted = useMemo(() => {
        const valid = Array.isArray(filtered)
            ? filtered.filter((d) => d && typeof d.Timestamp?.toDate === 'function')
            : [];
        return [...valid].sort((a, b) => b.Timestamp.toDate() - a.Timestamp.toDate());
    }, [filtered]);

    const count = Array.isArray(filtered) ? filtered.length : 0;
    const last = sorted[0] || null;
    const lastDays = last ? daysSince(last.Timestamp) : null;
    const recent = sorted.slice(0, topN);

    // De lijst staat op datum aflopend, dus `last` is het document met de
    // hoogste datum. Bij taken kan dat een GEPLANDE datum zijn: die ligt in de
    // toekomst en telt daarom negatief. 'laatste -2 dagen geleden' bestaat niet.
    // AI-CONTEXT: See docs/pitfalls.md#ui-negative-day-count-reads-as-days-ago
    const isPlanned = lastDays !== null && lastDays < 0;

    // Drie toestanden, en 'leeg' is er maar één van. De regels weigeren een
    // read op een omgeving waar dit account geen lid van is; die weigering
    // levert een lege lijst op, niet een fout. Zonder onderscheid staat er
    // 'nog niets geregistreerd' bij een omgeving die vol data zit.
    // AI-CONTEXT: See docs/inbox.md Bug #133
    // AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
    const isDenied = error?.code === 'permission-denied';
    const hasFailed = Boolean(error) && !isDenied;
    const isLoading = !hasFailed && !isDenied && (loading || !compagnyId || queryLoading || !Array.isArray(docs));

    return (
        <section className="score-explain-card">
            <header className="score-explain-card__header">
                <span className="project-detail-status-label">{label}</span>
                <span className="score-explain-card__value">
                    {isLoading ? <Spinner /> : error ? '—' : (
                        <>
                            {count}
                            {lastDays !== null && (
                                <span className={`last-entries-card__age ${daysColorClass(lastDays)}`}>
                                    ({formatDayAge(lastDays)})
                                </span>
                            )}
                        </>
                    )}
                </span>
            </header>
            <div className="score-explain-card__body">
                {isLoading ? (
                    <p className="score-explain-card__placeholder">Laden…</p>
                ) : isDenied ? (
                    <p className="score-explain-card__placeholder" role="alert">
                        Geen toegang tot deze omgeving met dit account — start een sessie om mee te kijken.
                    </p>
                ) : hasFailed ? (
                    <p className="score-explain-card__placeholder" role="alert">
                        Deze gegevens zijn nu niet op te halen.
                    </p>
                ) : count === 0 ? (
                    <p className="score-explain-card__placeholder">Nog geen {nounPlural || `${noun}en`} geregistreerd.</p>
                ) : (
                    <>
                        <p className="score-explain-card__decision">
                            {count} {count === 1 ? noun : (nounPlural || `${noun}en`)} totaal
                            {lastDays !== null && (isPlanned
                                ? ` — laatste staat gepland over ${Math.abs(lastDays)} ${Math.abs(lastDays) === 1 ? 'dag' : 'dagen'}`
                                : ` — laatste ${lastDays} ${lastDays === 1 ? 'dag' : 'dagen'} geleden`)}
                        </p>
                        <ul className="score-explain-card__signals">
                            <li>
                                Laatste {noun}: <strong>{formatDate(last.Timestamp)}</strong>
                                {last.Type && ` — type: ${last.Type}`}
                            </li>
                            {recent.length > 1 && recent.slice(1).map((entry) => (
                                <li key={entry.docid}>
                                    {formatDate(entry.Timestamp)}
                                    {entry.Type && <span className="last-entries-card__type"> — {entry.Type}</span>}
                                </li>
                            ))}
                        </ul>
                    </>
                )}
            </div>
        </section>
    );
};

export default LastEntriesCard;
